"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { Icon } from "@templates/design-system";
import { useState, type FormEvent } from "react";
import type { ProjectTypeOption } from "./ProjectBriefFormBlock.props";

const fieldLabelClass = "daylight-label-micro mb-3 block text-northsea";

const fieldClass =
  "w-full border-b border-border bg-transparent pb-2 text-lg text-graphite outline-none transition-colors duration-200 placeholder:text-northsea/60 hover:border-input focus:border-primary aria-[invalid=true]:border-destructive";

const sentenceControlClass =
  "border-b-2 border-primary bg-transparent pb-1 font-daylight-display text-primary outline-none transition-colors duration-200 placeholder:text-northsea/50 focus:border-graphite";

/** Connective between the project type and the location inside the brief sentence. */
const SENTENCE_CONNECTOR = "IN";

interface ProjectBriefFormProps {
  sentencePrefix?: string | null;
  projectTypes: ProjectTypeOption[];
  locationLabel?: string | null;
  nameLabel?: string | null;
  emailLabel?: string | null;
  detailsLabel?: string | null;
  submitLabel: string;
  successNote?: string | null;
}

export const ProjectBriefForm = ({
  detailsLabel,
  emailLabel,
  locationLabel,
  nameLabel,
  projectTypes,
  sentencePrefix,
  submitLabel,
  successNote,
}: ProjectBriefFormProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="border border-border p-8 md:p-12"
        role="status"
      >
        <p className="daylight-label mb-4 text-primary">{submitLabel}</p>
        <p className="max-w-xl text-lg text-graphite">
          {successNote || "Vielen Dank — wir melden uns in Kürze mit einer ersten Einschätzung."}
        </p>
      </div>
    );

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSubmitting(true);
    setFailed(false);
    try {
      const client = getBaseApiClient();
      if (!client) throw new Error("contact-client-unavailable");
      const location = String(data.get("location") || "").trim();
      const details = String(data.get("details") || "").trim();
      await client.contact.submit({
        email: String(data.get("email") || "").trim(),
        message: location ? `Standort: ${location}\n\n${details}` : details,
        name: String(data.get("name") || "").trim(),
        sourcePath: window.location.pathname,
        subject: String(data.get("projectType") || "").trim() || undefined,
      });
      setSucceeded(true);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="border border-border" onSubmit={submit}>
      <div className="border-b border-border p-8 md:p-12">
        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-3 font-daylight-display text-[clamp(1.5rem,1.6vw_+_0.9rem,1.875rem)] leading-[1.4] text-graphite">
          <span>{sentencePrefix || "WIR PLANEN EIN"}</span>
          <span className="relative inline-block">
            <select
              aria-label="Projekttyp"
              className={`${sentenceControlClass} cursor-pointer appearance-none pr-8`}
              defaultValue={projectTypes[0]?.label}
              name="projectType"
              required
            >
              {projectTypes.map((option) => (
                <option key={option.label} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
            <Icon
              className="pointer-events-none absolute right-0 top-1/2 size-4 -translate-y-1/2 text-primary"
              name="chevron-down"
            />
          </span>
          <span className="daylight-label text-northsea">{SENTENCE_CONNECTOR}</span>
          <input
            aria-label={locationLabel || "STANDORT"}
            className={`${sentenceControlClass} w-40 px-2`}
            name="location"
            placeholder={locationLabel || "STANDORT"}
            required
            type="text"
          />
          <span>.</span>
        </p>
      </div>
      <div className="grid grid-cols-1 border-b border-border md:grid-cols-2">
        <div className="border-b border-border p-8 md:border-b-0 md:border-r md:p-12">
          <label className={fieldLabelClass} htmlFor="brief-name">
            {nameLabel || "ANSPRECHPARTNER"}
          </label>
          <input
            className={fieldClass}
            id="brief-name"
            name="name"
            placeholder="Name"
            required
            type="text"
          />
        </div>
        <div className="p-8 md:p-12">
          <label className={fieldLabelClass} htmlFor="brief-email">
            {emailLabel || "E-MAIL"}
          </label>
          <input
            className={fieldClass}
            id="brief-email"
            name="email"
            placeholder="name@unternehmen.de"
            required
            type="email"
          />
        </div>
      </div>
      <div className="border-b border-border p-8 md:p-12">
        <label className={fieldLabelClass} htmlFor="brief-details">
          {detailsLabel || "TECHNISCHE DETAILS / LASTFALL"}
        </label>
        <textarea
          className={`${fieldClass} min-h-32 resize-none`}
          id="brief-details"
          name="details"
          placeholder="Leistung, Medium, Umgebungsbedingungen, Zeitrahmen …"
          rows={4}
        />
      </div>
      {failed ? (
        <p
          aria-live="assertive"
          className="border-b border-border p-8 text-base text-destructive md:px-12"
          role="alert"
        >
          Senden fehlgeschlagen — bitte versuchen Sie es erneut.
        </p>
      ) : null}
      <button
        className="group/submit flex w-full items-center justify-between gap-4 bg-primary px-8 py-8 text-primary-foreground transition-colors duration-200 hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-graphite disabled:opacity-60 md:px-12"
        disabled={submitting}
        type="submit"
      >
        <span className="font-daylight-display text-xl font-semibold tracking-tight md:text-2xl">
          {submitting ? "…" : submitLabel}
        </span>
        <span
          aria-hidden="true"
          className="daylight-label transition-transform duration-200 ease-out group-hover/submit:translate-x-2"
        >
          →
        </span>
      </button>
    </form>
  );
};

ProjectBriefForm.displayName = "Block::ProjectBriefForm";
